import { ActivityType } from "../models/enumerations/activity-type";
import { GroupIds } from "../models/enumerations/group-ids";
import { CorrectiveProgramInput } from "../models/interfaces/corrective-exercises/corrective-program-input";
import { UserCorrectiveProgram } from "../models/interfaces/corrective-exercises/user-corrective-program";
import { MovementScore } from "../models/interfaces/scores/movement-score";
import { IUser } from "../models/interfaces/assessment-user";
import DateUtil from "./date-util";
import { FirebaseUtils } from "./firebase-utils";
import MskScoreUtil from "./msk-score-util";
import CorrectiveProgramsService from "./services/corrective-programs-service/corrective-programs-serivce";
import UserCorrectiveProgramsService from "./services/corrective-programs-service/user-corrective-programs-service";
import WellnessProgramService from "./services/wellness-program-service";
import { CorrectiveProgramRoutine } from "../models/interfaces/corrective-exercises/corrective-program-routine";
import { createLogger } from "./logger";

const log = createLogger("CorrectiveProgramUtils");

const EXTRA_ID = "EXTRA_100_100"; // this is the id from FMS CMS

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

/**
 * Marks the specified corrective exercise as complete
 * @param   {string} userId - User who is completing an exercise day
 * @param   {number} day - 1-based day to mark as complete
 * @returns {Promise<void>}
 */
const finishDay = async (
    userId: string,
    day: number,
    currentIdentity?: IUser
): Promise<void> => {
    day = parseInt(day.toString());
    let userCorrectiveProgram = await _getCurrentUserCorrectiveProgram(
        userId
    );
    _log(
        `finishDay() userId=${userId} day=${day} userCorrectiveProgram:`,
        userCorrectiveProgram
    );
    if (userCorrectiveProgram == null) {
        return;
    }

    if (userCorrectiveProgram.completedDateMs == null) {
        userCorrectiveProgram.completedDateMs = [];
    }

    if (userCorrectiveProgram.completedDateMs.length >= day) {
        // set existing index
        const index = day - 1;
        if (userCorrectiveProgram.completedDateMs[index] == null) {
            userCorrectiveProgram.completedDateMs[index] =
                DateUtil.getCurrentMsDate();
        }
    } else {
        // add date to array
        while (userCorrectiveProgram.completedDateMs.length < day) {
            const completedDayMs =
                userCorrectiveProgram.completedDateMs.length === day - 1
                    ? DateUtil.getCurrentMsDate()
                    : 0;
            userCorrectiveProgram.completedDateMs.push(completedDayMs);
        }
    }

    // save changes
    const programId = userCorrectiveProgram.id;
    UserCorrectiveProgramsService.update(
        userCorrectiveProgram,
        currentIdentity
    ).catch((err) => {
        log.error("Failed to save corrective program day completion", {
            exception: err,
            day,
            programId,
        });
    });

    log.info("Corrective program day completed", {
        day,
        programId,
        completedDayCount: userCorrectiveProgram.completedDateMs?.length,
    });

    // delete today cache
    // TodayService.deleteToday(userId);

    // add user activity
    FirebaseUtils.logUserActivity(userId, ActivityType.Completion);
};

/**
 * Marks the specified corrective exercise as complete
 * @param   {string} userId - User who is completing an exercise day
 * @param   {number} day - 1-based day to mark as complete
 * @returns {Promise<void>}
 */
const finishDayExtra = async (
    userId: string,
    day: number,
    currentIdentity?: IUser
): Promise<void> => {
    day = parseInt(day.toString());
    let userCorrectiveProgram = await _getCurrentUserExtraCorrectiveProgram(
        userId
    );
    _log(
        `finishDay() userId=${userId} day=${day} userCorrectiveProgram:`,
        userCorrectiveProgram
    );
    if (userCorrectiveProgram == null) {
        return;
    }

    if (userCorrectiveProgram.completedDateMs == null) {
        userCorrectiveProgram.completedDateMs = [];
    }

    if (userCorrectiveProgram.completedDateMs.length >= day) {
        // set existing index
        const index = day - 1;
        if (userCorrectiveProgram.completedDateMs[index] == null) {
            userCorrectiveProgram.completedDateMs[index] =
                DateUtil.getCurrentMsDate();
        }
    } else {
        // add date to array
        while (userCorrectiveProgram.completedDateMs.length < day) {
            const completedDayMs =
                userCorrectiveProgram.completedDateMs.length === day - 1
                    ? DateUtil.getCurrentMsDate()
                    : 0;
            userCorrectiveProgram.completedDateMs.push(completedDayMs);
        }
    }

    // save changes
    const programId = userCorrectiveProgram.id;
    UserCorrectiveProgramsService.update(
        userCorrectiveProgram,
        currentIdentity
    ).catch((err) => {
        log.error("Failed to save extra corrective program day completion", {
            exception: err,
            day,
            programId,
        });
    });

    log.info("Corrective program extra day completed", {
        day,
        programId,
        completedDayCount: userCorrectiveProgram.completedDateMs?.length,
    });

    // delete today cache
    // TodayService.deleteToday(userId);

    // add user activity
    FirebaseUtils.logUserActivity(userId, ActivityType.Completion);
};

/**
 * Returns today's corrective exercise
 * @param    {string} userId - User who is getting an exercise
 * @returns {Promise<CorrectiveProgramRoutine | null>} A promise for the current corrective exercise
 */
const getTodaysCorrectiveProgramRoutine = async (
    userId: string,
    dayToShow?: number,
): Promise<CorrectiveProgramRoutine | null> => {
    let userCorrectiveProgram = await _getCurrentUserCorrectiveProgram(
        userId
    );
    _log(
        `getTodaysCorrectiveProgram("${userId}") existing userCorrectiveProgram:`,
        userCorrectiveProgram
    );

    // we don't want to create a new corrective if today's exercise is an extra
    // (road to wellness)
    if (userCorrectiveProgram == null) {
        const extra = await _getCurrentUserExtraCorrectiveProgram(userId);
        if (extra) {
            return null;
        }
    }

    // create UserCorrectiveExercise and return day 1
    if (userCorrectiveProgram == null) {
        // return routine.day1Exercise;
        return null;
    }



    // validate that the user currently has a routine
    if (userCorrectiveProgram.routineId == null) {
        return null;
    }

    // Account for missed days
    const wellnessProgram = await WellnessProgramService.getCurrentByUserId(userId);
    let exerciseDay: number | undefined;

    // My test account some how had multiple current WP. I believe its due to testing and causing issues on staging.
    // If this truly is an issue in prod, here, we can check if wp.length > 1 and delete old true WPs.
    // update: if you do an individual assessment retake, we dont cancel WPs. This is a dev feature so users shouldn't ever do this.
    // by individual assessment, i mean you can add a groupId to the URL and do ONLY a behavioral assessment for example. This is where it's not cancelled.
    if (wellnessProgram.length !== 0 && wellnessProgram[0]) {
        exerciseDay = dayToShow !== undefined ? dayToShow : wellnessProgram[0].currentDay;

        if ((userCorrectiveProgram.completedDateMs.length + 1) !== wellnessProgram[0].currentDay) {
            const missedDays = exerciseDay - 1 - userCorrectiveProgram.completedDateMs.length;

            if (missedDays > 0) {
                for (let i = 0; i < missedDays; i++) {
                    if (userCorrectiveProgram.completedDateMs.length === userCorrectiveProgram.routineDays) {
                        break;
                    }
                    userCorrectiveProgram.completedDateMs.push(0);
                }

                await UserCorrectiveProgramsService.update(userCorrectiveProgram);
                log.info("Corrective program missed days backfilled", {
                    missedDays,
                    exerciseDay,
                    programId: userCorrectiveProgram.id,
                });
            }
        }
    }
    // returning null because user has no wellness program
    else {
        return null;
    }

    // get exercise
    const wasCompletedToday = _wasCompletedToday(userCorrectiveProgram);

    // mark complete
    if (
        _canMarkNotCurrent(
            wasCompletedToday,
            userCorrectiveProgram.completedDateMs,
            userCorrectiveProgram.routineDays
        )
    ) {
        // This if is only meant for any day after the user completed their final corrective exercise (7/7)

        return null;
    }

    const correctiveProgramRoutineInput = {
        programId: userCorrectiveProgram.routineId,
        day: exerciseDay,
    };
    const correctiveProgramRoutine = await CorrectiveProgramsService.getCorrectiveProgramRoutine(
        correctiveProgramRoutineInput
    );
    if (correctiveProgramRoutine.errors != null && correctiveProgramRoutine.errors.length >= 1) {
        correctiveProgramRoutine.errors = [
            `Error loading videos for Corrective Program Routine ${correctiveProgramRoutineInput.programId
            } Day ${correctiveProgramRoutineInput.day}: ${correctiveProgramRoutine.errors.join("; ")}`,
        ];
    }
    correctiveProgramRoutine.routineDays = userCorrectiveProgram.routineDays;
    // If dayToShow is different from the current day (e.g. it's the next day) then don't show the exercise as complete
    correctiveProgramRoutine.isComplete = (!dayToShow || dayToShow === wellnessProgram[0].currentDay) ? wasCompletedToday : false;
    return correctiveProgramRoutine;
};

/**
 * Returns today's extra corrective exercise
 * @param    {string} userId - User who is getting an exercise
 * @returns {Promise<CorrectiveProgramRoutine | null>} A promise for the current corrective exercise
 */
const getTodaysExtraCorrectiveProgramRoutine = async (
    userId: string,
    dayToShow?: number,
): Promise<CorrectiveProgramRoutine | null> => {
    let userCorrectiveProgram = await _getCurrentUserExtraCorrectiveProgram(
        userId
    );

    _log(
        `getTodaysExtraCorrectiveProgramRoutine("${userId}") existing userCorrectiveProgram:`,
        userCorrectiveProgram
    );

    // create UserCorrectiveExercise and return day 1
    if (userCorrectiveProgram == null) {
        // return routine.day1Exercise;
        return null;
    }

    // validate that the user currently has a routine
    if (userCorrectiveProgram.routineId == null) {
        return null;
    }

    // Account for missed days
    const wellnessProgram = await WellnessProgramService.getCurrentByUserId(userId);
    let exerciseDay: number | undefined;

    if (wellnessProgram.length !== 0 && wellnessProgram[0]) {
        exerciseDay = dayToShow !== undefined ? dayToShow : wellnessProgram[0].currentDay;

        if ((userCorrectiveProgram.completedDateMs.length + 1) !== wellnessProgram[0].currentDay) {
            const missedDays = exerciseDay - 1 - userCorrectiveProgram.completedDateMs.length;

            if (missedDays > 0) {
                for (let i = 0; i < missedDays; i++) {
                    if (userCorrectiveProgram.completedDateMs.length === userCorrectiveProgram.routineDays) {
                        break;
                    }
                    userCorrectiveProgram.completedDateMs.push(0);
                }

                await UserCorrectiveProgramsService.update(userCorrectiveProgram);
                log.info("Corrective program extra missed days backfilled", {
                    missedDays,
                    exerciseDay,
                    programId: userCorrectiveProgram.id,
                });
            }
        }
    }
    else {
        return null;
    }

    // get exercise
    const wasCompletedToday = _wasCompletedToday(userCorrectiveProgram);

    // mark complete
    if (
        _canMarkNotCurrent(
            wasCompletedToday,
            userCorrectiveProgram.completedDateMs,
            userCorrectiveProgram.routineDays
        )
    ) {
        return null;
    }

    const correctiveProgramRoutineInput = {
        programId: userCorrectiveProgram.routineId,
        day: exerciseDay,
    };
    const correctiveProgramRoutine = await CorrectiveProgramsService.getCorrectiveProgramRoutine(
        correctiveProgramRoutineInput
    );
    if (correctiveProgramRoutine.errors != null && correctiveProgramRoutine.errors.length >= 1) {
        correctiveProgramRoutine.errors = [
            `Error loading videos for Corrective Program Routine ${correctiveProgramRoutineInput.programId
            } Day ${correctiveProgramRoutineInput.day}: ${correctiveProgramRoutine.errors.join("; ")}`,
        ];
    }
    correctiveProgramRoutine.routineDays = userCorrectiveProgram!.routineDays;
    // If dayToShow is different from the current day (e.g. it's the next day) then don't show the exercise as complete
    correctiveProgramRoutine.isComplete = (!dayToShow || dayToShow === wellnessProgram[0].currentDay) ? wasCompletedToday : false;

    return correctiveProgramRoutine;
};

/**
 * When a new movement score has been added, this changes the state of UserCorrectiveExercise
 * @param    {MovementScore} movementScore - new movement score
 * @param    {IUser} currentIdentity - user with the movement score
 * @returns {Promise<void>}
 */
const handleNewMovementScore = async (
    movementScore: MovementScore,
    currentIdentity: IUser
): Promise<void> => {
    // validate inputs
    if (currentIdentity?.id == null || movementScore?.percentage == null) {
        _log(
            `handleNewMovementScore() abort changes: userId=${currentIdentity?.id} movement%=${movementScore?.percentage}`
        );
        return;
    }

    // load current corrective exercise
    let currentCorrectiveProgram = await _getCurrentUserCorrectiveProgram(
        currentIdentity.id
    );
    if (currentCorrectiveProgram == null) {
        _log(
            `handleNewMovementScore() no current corrective for "${currentIdentity?.id}"`
        );
        return;
    }

    // if they have a current corrective program and its current, mark it not current
    if (currentCorrectiveProgram && currentCorrectiveProgram.isCurrent) {
        currentCorrectiveProgram.isCurrent = false;
        await UserCorrectiveProgramsService.update(
            currentCorrectiveProgram,
            currentIdentity
        );
        log.info("Corrective program deactivated after new movement score", {
            programId: currentCorrectiveProgram.id,
            routineId: currentCorrectiveProgram.routineId,
        });
    }
};

/**
 * When a new movement score has been added, this changes the state of UserCorrectiveExercise
 * @param    {MovementScore} movementScore - new movement score
 * @param    {IUser} currentIdentity - user with the movement score
 * @returns {Promise<void>}
 */
const handleNewMovementScoreById = async (
    movementScore: MovementScore,
    currentUserId: string
): Promise<void> => {
    // validate inputs
    if (currentUserId == null || movementScore?.percentage == null) {
        _log(
            `handleNewMovementScore() abort changes: userId=${currentUserId} movement%=${movementScore?.percentage}`
        );
        return;
    }

    // load current corrective exercise
    let currentCorrectiveProgram = await _getCurrentUserCorrectiveProgram(
        currentUserId
    );
    if (currentCorrectiveProgram == null) {
        _log(
            `handleNewMovementScore() no current corrective for "${currentUserId}"`
        );
        return;
    }

    // if they have a current corrective program and its current, mark it not current
    if (currentCorrectiveProgram && currentCorrectiveProgram.isCurrent) {
        currentCorrectiveProgram.isCurrent = false;
        await UserCorrectiveProgramsService.updateById(
            currentCorrectiveProgram,
            currentUserId
        );
        log.info("Corrective program deactivated after new movement score", {
            programId: currentCorrectiveProgram.id,
            routineId: currentCorrectiveProgram.routineId,
        });
    }
};

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Private Functions
// -----------------------------------------------------------------------------------------

const _log = (message?: any, ...optionalParams: any[]): void => {
    if (false) {
        log.debug(message, ...optionalParams);
    }
};

const _canMarkNotCurrent = (
    wasCompletedToday: boolean,
    completedDateMs: number[],
    programDays: number
): boolean => {
    if (wasCompletedToday) {
        return false;
    }
    if (completedDateMs == null || completedDateMs.length === 0) {
        return false;
    }
    return completedDateMs.length >= programDays;
};

const _createUserCorrectiveProgram = async (userId: string): Promise<any> => {
    const mskScore = await MskScoreUtil.getLatest(userId);

    if (mskScore?.movementScore?.percentage == null) {
        _log("_createUserCorrectiveProgram(): movement was not completed");
        return null;
    }

    // determine routine
    let focusArea = mskScore.movementScore.focusArea;
    if (focusArea == null && mskScore.movementScore.percentage === 100) {
        focusArea = {
            groupId: GroupIds.MOVEMENT_HEALTH,
            groupName: undefined,
            percentage: 100,
            score: 3,
        };
    }
    const correctiveProgramInput: CorrectiveProgramInput = {
        focusArea: focusArea,
    };
    _log("_createUserCorrectiveProgram(): correctiveProgramInput:", correctiveProgramInput);
    const correctiveProgram = await CorrectiveProgramsService.findProgram(
        correctiveProgramInput
    );
    _log("_createUserCorrectiveProgram(): routine:", correctiveProgram);
    const hasCorrectiveProgram: boolean = correctiveProgram != null && correctiveProgram.programId != null;

    // add UserCorrectiveExercise
    let userCorrectiveProgram: UserCorrectiveProgram = {
        userId: userId,
        routineDays: hasCorrectiveProgram ? correctiveProgram.programDays : 100,
        isCurrent: true,
        completedDateMs: [],
    };
    if (hasCorrectiveProgram) {
        userCorrectiveProgram.routineId = correctiveProgram.programId;
    }

    userCorrectiveProgram = await UserCorrectiveProgramsService.add(
        userCorrectiveProgram
    );

    log.info("Corrective program created", {
        programId: userCorrectiveProgram.id,
        routineId: userCorrectiveProgram.routineId,
        routineDays: userCorrectiveProgram.routineDays,
        hasCorrectiveProgram,
    });

    return {
        hasCorrectiveProgram: hasCorrectiveProgram,
        correctiveProgram: correctiveProgram,
        userCorrectiveProgram: userCorrectiveProgram,
    };
};

const _createUserExtraCorrectiveProgram = async (userId: string): Promise<any> => {
    const mskScore = await MskScoreUtil.getLatest(userId);

    if (mskScore?.movementScore?.percentage == null) {
        _log("_createUserExtraCorrectiveExercise(): movement was not completed");
        return null;
    }

    // determine routine
    let focusArea = mskScore.movementScore.focusArea;
    if (focusArea == null && mskScore.movementScore.percentage === 100) {
        focusArea = {
            groupId: GroupIds.MOVEMENT_HEALTH,
            percentage: 100,
            score: 3,
        };
    }
    const correctiveProgramInput: CorrectiveProgramInput = {
        focusArea: focusArea,
        existingMskScore: mskScore,
    };
    _log("_createUserExtraCorrectiveProgram(): correctiveProgramInput:", correctiveProgramInput);
    const correctiveProgram = await CorrectiveProgramsService.findExtraProgram(
        correctiveProgramInput
    );
    _log("_createUserExtraCorrectiveExercise(): routine:", correctiveProgram);
    const hasCorrectiveProgram: boolean = correctiveProgram != null && correctiveProgram.programId != null;

    // add UserCorrectiveExercise
    let userCorrectiveProgram: UserCorrectiveProgram = {
        userId: userId,
        routineDays: hasCorrectiveProgram ? correctiveProgram.programDays : 100,
        isCurrent: true,
        completedDateMs: [],
    };
    if (hasCorrectiveProgram) {
        userCorrectiveProgram.routineId = correctiveProgram.programId;
    }

    userCorrectiveProgram = await UserCorrectiveProgramsService.add(
        userCorrectiveProgram
    );

    log.info("Corrective program extra created", {
        programId: userCorrectiveProgram.id,
        routineId: userCorrectiveProgram.routineId,
        routineDays: userCorrectiveProgram.routineDays,
        hasCorrectiveProgram,
    });

    return {
        hasCorrectiveProgram: hasCorrectiveProgram,
        correctiveProgram: correctiveProgram,
        userCorrectiveProgram: userCorrectiveProgram,
    };
};

const _wasCompletedToday = (
    userCorrectivePrograms: UserCorrectiveProgram | null
): boolean => {
    if (
        userCorrectivePrograms?.completedDateMs == null ||
        userCorrectivePrograms.completedDateMs.length === 0
    ) {
        return false;
    }

    const lastCompletedMs =
        userCorrectivePrograms.completedDateMs[
        userCorrectivePrograms.completedDateMs.length - 1
        ];
    return DateUtil.isToday(lastCompletedMs);
};

const _getCurrentUserCorrectiveProgram = async (
    userId: string
): Promise<UserCorrectiveProgram | null> => {
    let userCorrectiveProgram: UserCorrectiveProgram | null = null;

    try {
        const userCorrectivePrograms =
            await UserCorrectiveProgramsService.getCurrentByUserId(userId);
        if (
            userCorrectivePrograms != null &&
            userCorrectivePrograms.length >= 1
        ) {
            userCorrectiveProgram = userCorrectivePrograms[0];
        }
    } catch (e) {
        log.error(`ERROR: Could not load user "${userId}" corrective program`, {
            exception: e,
            userId,
        });
    }

    return userCorrectiveProgram;
};

const _getCurrentUserExtraCorrectiveProgram = async (
    userId: string
): Promise<UserCorrectiveProgram | null> => {
    let userCorrectiveProgram: UserCorrectiveProgram | null = null;

    try {
        const userCorrectivePrograms =
            await UserCorrectiveProgramsService.getCurrentExtraByUserId(userId);
        if (
            userCorrectivePrograms != null &&
            userCorrectivePrograms.length >= 1
        ) {
            userCorrectiveProgram = userCorrectivePrograms[0];
        }
    } catch (e) {
        log.error(`ERROR: Could not load user "${userId}" extra corrective program`, {
            exception: e,
            userId,
        });
    }
    return userCorrectiveProgram;
};

// #endregion Private Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export const CorrectiveProgramUtils = {
    EXTRA_ID,
    finishDay,
    finishDayExtra,
    getTodaysCorrectiveProgramRoutine,
    getTodaysExtraCorrectiveProgramRoutine,
    handleNewMovementScore,
    handleNewMovementScoreById,
    _createUserCorrectiveProgram,
    _createUserExtraCorrectiveProgram
};

// #endregion Exports
